import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, Plus, Minus, Heart, ChevronLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Breadcrumb } from '../components/common/Breadcrumb';
import { SocialShare } from '../components/common/SocialShare';
import ImageZoom from '../components/product/ImageZoom';
import { ProductReviews } from '../components/product/ProductReviews';
import { ReviewForm } from '../components/product/ReviewForm';
import { ProductTags } from '../components/product/ProductTags';
import { ProductBadges } from '../components/product/ProductBadges';
import { useProduct, useProducts, useCategories } from '../hooks/useData';
import { useCart } from '../contexts/CartContext';
import { useWishlist } from '../contexts/WishlistContext';
import { useRecentlyViewed } from '../hooks/useRecentlyViewed';
import { formatPrice, calculateDiscount } from '../lib/utils';
import { ProductCard } from '../components/product/ProductCard';
import { toast } from 'sonner';

export const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { product, loading } = useProduct(id);
  const { products } = useProducts();
  const { categories } = useCategories();
  const { addToCart } = useCart();
  const { addToWishlist, isInWishlist, removeFromWishlist } = useWishlist();
  const { addToRecentlyViewed, recentProducts } = useRecentlyViewed();
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    if (product) {
      addToRecentlyViewed(product);
    }
  }, [product]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-serif text-3xl font-bold mb-4">Product not found</h2>
          <Button onClick={() => navigate('/shop')}>Back to Shop</Button>
        </div>
      </div>
    );
  }

  const finalPrice = product.discount > 0
    ? calculateDiscount(product.price, product.discount)
    : product.price;

  const relatedProducts = products
    .filter(p => p.category_id === product.category_id && p.id !== product.id)
    .slice(0, 4);

  const currentCategory = categories.find(c => c.id === product.category_id);

  const handleAddToCart = () => {
    addToCart(product, quantity);
  };

  const images = product.images && product.images.length > 0
    ? product.images
    : [];

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Shop', href: '/shop' },
    ...(currentCategory ? [{ label: currentCategory.name, href: `/category/${currentCategory.id}` }] : []),
    { label: product.name }
  ];

  return (
    <div className="min-h-screen py-8">
      <div className="container-custom">
        {/* Breadcrumb */}
        <Breadcrumb items={breadcrumbItems} />

        {/* Product Details */}
        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          {/* Images with Zoom */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="sticky top-24">
              <ImageZoom images={images} productName={product.name} />
            </div>
          </motion.div>

          {/* Details */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="space-y-6">
              <div>
                <p className="text-sm text-muted-foreground mb-2">{product.brand}</p>
                <h1 className="font-serif text-4xl sm:text-5xl font-bold text-foreground mb-4">
                  {product.name}
                </h1>
                
                {/* Product Badges */}
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <ProductBadges product={product} />
                  {product.discount > 0 && (
                    <Badge variant="destructive">Save {product.discount}%</Badge>
                  )}
                  {product.stock_quantity > 0 ? (
                    <Badge variant="success">In Stock</Badge>
                  ) : (
                    <Badge variant="destructive">Out of Stock</Badge>
                  )}
                </div>

                {/* Product Tags */}
                <ProductTags product={product} className="mb-4" />
              </div>

              <div className="border-y py-6">
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="text-4xl font-bold text-primary">
                    {formatPrice(finalPrice)}
                  </span>
                  {product.discount > 0 && (
                    <span className="text-xl text-muted-foreground line-through">
                      {formatPrice(product.price)}
                    </span>
                  )}
                </div>
                {product.stock_quantity > 0 && (
                  <p className="text-sm text-muted-foreground">
                    {product.stock_quantity} units available
                  </p>
                )}
              </div>

              <div>
                <h3 className="font-semibold text-lg mb-3">Description</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {product.description}
                </p>
              </div>

              {/* Quantity & Actions */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Quantity</label>
                  <div className="flex items-center space-x-4">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="text-xl font-semibold w-12 text-center">{quantity}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setQuantity(Math.min(product.stock_quantity, quantity + 1))}
                      disabled={quantity >= product.stock_quantity}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    size="lg"
                    className="flex-1"
                    onClick={handleAddToCart}
                    disabled={product.stock_quantity <= 0}
                  >
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Add to Cart
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="w-12 h-12"
                    onClick={() => {
                      if (isInWishlist(product.id)) {
                        removeFromWishlist(product.id);
                      } else {
                        addToWishlist(product);
                      }
                    }}
                  >
                    <Heart className={`w-5 h-5 ${isInWishlist(product.id) ? 'fill-primary text-primary' : ''}`} />
                  </Button>
                </div>

                {/* Social Share */}
                <div className="pt-4 border-t">
                  <p className="text-sm font-medium mb-3">Share this product:</p>
                  <SocialShare product={product} url={window.location.href} />
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Additional Information */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {product.ingredients && (
            <Card>
              <CardHeader>
                <CardTitle>Ingredients</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {product.ingredients}
                </p>
              </CardContent>
            </Card>
          )}
          {product.usage_instructions && (
            <Card>
              <CardHeader>
                <CardTitle>How to Use</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {product.usage_instructions}
                </p>
              </CardContent>
            </Card>
          )}
          {product.warnings && (
            <Card>
              <CardHeader>
                <CardTitle>Warnings</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {product.warnings}
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Product Reviews */}
        {product.rating && (
          <div className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-foreground mb-8">
              Customer Reviews
            </h2>
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <ProductReviews 
                  productId={product.id} 
                  rating={product.rating}
                  reviewsCount={product.reviews_count}
                />
              </div>
              <div className="lg:col-span-1">
                <ReviewForm productId={product.id} />
              </div>
            </div>
          </div>
        )}

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-foreground mb-8">
              You May Also Like
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        )}

        {/* Recently Viewed */}
        {recentProducts.length > 0 && (
          <div>
            <h2 className="font-serif text-3xl font-bold text-foreground mb-8">
              Recently Viewed
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {recentProducts.slice(0, 4).map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};